import axios from "axios";
import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";

const TICKERS_LANDING = ["SPY", "QQQ", "DIA", "GLD", "BND", "AAPL", "QBTS", "DAL"];
const TICKERS_DASH = ["SPY", "QQQ", "DIA","GLD", "BND", "AAPL", "QBTS", "DAL"];
const FIREBASE_UID = "vAXgbdT5z4WTMDJ1BTgXeZgaCOH2";

// Utility function to check if US market is open (Eastern Time)
function isMarketOpen() {
  const now = new Date();
  // Convert to US/Eastern time offset
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  // New York is UTC-4 (EDT) or UTC-5 (EST)
  // We'll use -4 for simplicity (EDT, daylight savings)
  // For more accuracy, use a timezone library like luxon or moment-timezone
  const offset = -4; // EDT
  const ny = new Date(utc + 3600000 * offset);
  const day = ny.getDay();
  const hour = ny.getHours();
  const minute = ny.getMinutes();
  // Market open: Mon-Fri, 9:30am to 4:00pm
  if (day === 0 || day === 6) return false; // Sunday or Saturday
  if (hour < 9 || (hour === 9 && minute < 30)) return false;
  if (hour > 16 || (hour === 16 && minute > 0)) return false;
  return true;
}

export default function TickerTape({ isLanding }) {
  const TICKERS = isLanding ? TICKERS_LANDING : TICKERS_DASH;
  const backendBaseURL =
    process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
      ? "http://127.0.0.1:5000"
      : "https://flask-backend-52245432644.us-central1.run.app";
  const frontendBaseURL =
    process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
      ? "http://localhost:8080"
      : "https://marketcrunch.ai";
  const [tickerData, setTickerData] = useState(null);
  const [predictionData, setPredictionData] = useState({});

  // Fetch live prices
  useEffect(() => {
    const fetchData = async () => {
      const cachedData = localStorage.getItem("tickerTapeData");
      const parsedData = JSON.parse(cachedData);
      const cacheTimestamp = localStorage.getItem("tickerTapeTimestamp");
      const now = new Date().getTime();

      if (
        parsedData &&
        parsedData.length > 0 &&
        parsedData.every((ticker) => !isNaN(ticker[1]) && !isNaN(ticker[2])) &&
        cacheTimestamp &&
        now - parseInt(cacheTimestamp) < 600000
      ) {
        setTickerData(parsedData);
        return;
      }

      try {
        const response = await axios.post(`${backendBaseURL}/ticker-tape`, {
          tickers: TICKERS,
        });
        const newData = response.data.data;
        setTickerData(newData);

        if (newData.every((ticker) => !isNaN(ticker[1]) && !isNaN(ticker[2]))) {
          localStorage.setItem("tickerTapeData", JSON.stringify(newData));
          localStorage.setItem("tickerTapeTimestamp", now.toString());
        }
      } catch (error) {
        console.error("Error fetching ticker data:", error);
        if (parsedData) {
          setTickerData(parsedData);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [isLanding]);

  // Fetch predictions for each ticker (only on landing)
  useEffect(() => {
    if (!isLanding || !tickerData) return;
    const fetchPredictions = async () => {
      const results = {};
      await Promise.all(
        TICKERS.map(async (ticker) => {
          try {
            const response = await axios.post(
              `${backendBaseURL}/train-and-predict`,
              {
                ticker,
                firebase_uid: FIREBASE_UID,
              }
            );
            results[ticker] = response.data;
          } catch (e) {
            results[ticker] = null;
          }
        })
      );
      setPredictionData(results);
    };
    fetchPredictions();
    // eslint-disable-next-line
  }, [tickerData, isLanding]);

  return (
    <div
      style={{
        width:
          window.innerWidth <= 485 && isLanding
            ? "95%"
            : isLanding
            ? "80%"
            : "100%",
        backgroundColor: isLanding ? "white" : "transparent",
        backgroundColor: isLanding ? "white" : "transparent",
      }}
    >
      {tickerData && (
        <Marquee
          style={{
            height: isLanding ? 90 : 40,
          }}
          speed={isLanding ? 30 : undefined}
          pauseOnHover={true}
        >
          {tickerData.map((ticker) => {
            if (!isNaN(ticker[1]) && !isNaN(ticker[2])) {
              // LANDING: Show prediction, accuracy, confidence
              if (isLanding) {
                const pred = predictionData[ticker[0]];
                return (
                  <a
                    href={`${frontendBaseURL}/analyze?t=${ticker[0]}&source=discovery-tt-hme`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                    key={ticker[0]}
                  >
                    <div style={{ marginRight: 48, minWidth: 220 }}>
                      <span
                        style={{
                          fontFamily: "Inter",
                          fontWeight: 600,
                          color: "black",
                        }}
                      >{`${ticker[0]}: `}</span>
                      <span
                        style={{
                          fontFamily: "Inter",
                          backgroundColor:
                            parseFloat(ticker[2]) > 0
                              ? "rgba(19, 156, 141, 0.075)"
                              : parseFloat(ticker[2]) < 0
                              ? "rgba(255, 0, 0, 0.075)"
                              : "rgba(85, 85, 85, 0.075)",
                          color:
                            parseFloat(ticker[2]) > 0
                              ? "#139C8D"
                              : parseFloat(ticker[2]) < 0
                              ? "rgb(255, 76, 97)"
                              : "#555",
                          padding: "5px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        {`$${ticker[1]} (${ticker[2]}%)`}
                      </span>
                      {pred && (
                        <div style={{ marginTop: 6, paddingBottom: 10, textAlign: 'left', marginLeft: 16 }}>
                          {(() => {
                            const percent = Number(pred.predicted_change) * 100;
                            const currentPrice = Number(ticker[1]);
                            const percentChange = Number(ticker[2]);
                            // Use currentPrice as prevClose after market close, otherwise use formula
                            const prevClose = isMarketOpen()
                              ? currentPrice / (1 + percentChange / 100)
                              : currentPrice;
                            const predictedPrice = prevClose * (1 + Number(pred.predicted_change));
                            // Determine color and arrow for percent change
                            let percentColor = "#000";
                            let arrowIcon = null;
                            if (percent < -0.03) {
                              percentColor = "#D32F2F";
                              arrowIcon = (
                                <i
                                  className="fa-solid fa-arrow-trend-down"
                                  style={{ color: "#D32F2F", marginRight: 6, fontSize: 15, verticalAlign: "middle" }}
                                />
                              );
                            } else if (percent > 0.03) {
                              percentColor = "#388E3C";
                              arrowIcon = (
                                <i
                                  className="fa-solid fa-arrow-trend-up"
                                  style={{ color: "#388E3C", marginRight: 6, fontSize: 15, verticalAlign: "middle" }}
                                />
                              );
                            } else {
                              // Flat
                              percentColor = "#000";
                              arrowIcon = (
                                <i
                                  className="fa-solid fa-minus"
                                  style={{ color: "#000", marginRight: 6, fontSize: 15, verticalAlign: "middle" }}
                                />
                              );
                            }
                            return (
                              <span style={{ fontWeight: 600, fontSize: 13, color: "black", display: "flex", alignItems: "center", textAlign: 'left' }}>
                                {arrowIcon}
                                <span style={{ color: 'black' }}>Prediction: </span>
                                <span style={{ color: percentColor }}>${predictedPrice.toFixed(2)} ({percent.toFixed(2)}%)</span>
                              </span>
                            );
                          })()}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              color: 'black',
                              fontWeight: 500,
                              fontSize: 13,
                              textAlign: 'left',
                            }}
                          >
                            <span style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              marginRight: 6,
                              background: (() => {
                                let accuracy = Math.round(pred.accuracy * 100);
                                if (accuracy > 84) accuracy = 84;
                                if (accuracy < 0) accuracy = 0;
                                const level = Math.max(1, Math.min(10, Math.ceil((accuracy / 84) * 10)));
                                const gaugeColors = {
                                  1: '#D32F2F',
                                  2: '#E57373',
                                  3: '#F57C00',
                                  4: '#FFB74D',
                                  5: '#BDBDBD',
                                  6: '#FFF176',
                                  7: '#AED581',
                                  8: '#81C784',
                                  9: '#4CAF50',
                                  10: '#388E3C',
                                };
                                return gaugeColors[level];
                              })(),
                            }} />
                            <span style={{ color: 'black' }}>
                              {(() => {
                                let accuracy = Math.round(pred.accuracy * 100);
                                return (accuracy > 84 ? 84 : accuracy) + '% Accuracy (last 90d)';
                              })()}
                            </span>
                          </div>
                          {pred.confidence_score !== undefined && (() => {
                            const conf = Number(pred.confidence_score) * 100;
                            let gaugeColor = "#FFB74D";
                            if (conf > 75) gaugeColor = "#388E3C";
                            else if (conf < 50) gaugeColor = "#D32F2F";
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  color: 'black',
                                  fontWeight: 500,
                                  fontSize: 13,
                                  textAlign: 'left',
                                }}
                              >
                                <span style={{
                                  display: 'inline-block',
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  marginRight: 6,
                                  background: gaugeColor,
                                }} />
                                <span style={{ color: 'black' }}>AI-Model Confidence</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </a>
                );
              }
              // DASHBOARD: Show only price as before
              return (
                <a
                  href={`${frontendBaseURL}/analyze?t=${ticker[0]}&source=discovery-tt-dash`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  key={ticker[0]}
                >
                  <div style={{ marginRight: 20 }}>
                    <span
                      style={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        color: "#1D3557",
                      }}
                    >{`${ticker[0]}: `}</span>
                    <span
                      style={{
                        fontFamily: "Inter",
                        backgroundColor:
                          parseFloat(ticker[2]) > 0.0
                            ? "rgba(19, 156, 141, 0.075)"
                            : parseFloat(ticker[2]) < 0.0
                            ? "rgba(255, 0, 0, 0.075)"
                            : "rgba(85, 85, 85, 0.075)",
                        color:
                          parseFloat(ticker[2]) > 0.0
                            ? "#139C8D"
                            : parseFloat(ticker[2]) < 0.0
                            ? "rgb(255, 76, 97)"
                            : "#555",
                        padding: "5px 10px",
                        borderRadius: "5px",
                      }}
                    >
                      {`$${
                        parseFloat(ticker[1])
                          ? parseFloat(ticker[1]).toFixed(2)
                          : ""
                      } (${ticker[2]}%)`}
                    </span>
                  </div>
                </a>
              );
            }
            return null;
          })}
        </Marquee>
      )}
      {!tickerData && (
        <Marquee>
          {TICKERS.map((ticker) => {
            return (
              <div key={ticker} style={{ marginRight: isLanding ? 48 : 20, minWidth: 220 }}>
                {ticker}: Loading...
              </div>
            );
          })}
        </Marquee>
      )}
    </div>
  );
}
